import React, { useEffect, useRef, useState } from 'react';
import {
    useTheme,
    Tabs,
    TabList,
    Tab,
    IconButton,
    Dropdown,
    MenuButton,
    Menu,
    MenuItem
} from '@mui/joy';
import {
    ChevronLeftFilledOutlined,
    ChevronRightFilledOutlined,
    TabCloseOutlined
} from "@mui-symbols-material/w300";
import {useTranslation} from "react-i18next";

interface AutoScrollTabsProps {
    value: string;
    tabs: { label: React.ReactNode; value: string; icon?: React.ReactNode, sticky?: boolean }[];
    onChange?: (event: React.SyntheticEvent | null, value: number | string | null) => void;
    onCloseAll?: () => void;
    onCloseOthers?: () => void;
}

const AutoScrollTabs: React.FC<AutoScrollTabsProps> = ({
                                                           value,
                                                           onChange,
                                                           tabs,
                                                           onCloseAll,
                                                           onCloseOthers
                                                       }) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const listRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const stickyTabs = tabs.filter((tab) => tab.sticky);
    const scrollableTabs = tabs.filter((tab) => !tab.sticky);

    const checkScroll = () => {
        const list = listRef.current;
        if (!list) return;
        setCanScrollLeft(list.scrollLeft > 0);
        setCanScrollRight(
            list.scrollLeft + list.clientWidth < list.scrollWidth - 1
        );
    };

    const scroll = (direction: 'left' | 'right') => {
        const list = listRef.current;
        if (!list) return;
        const amount = list.clientWidth / 1.5;
        list.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
    };

    useEffect(() => {
        checkScroll();
        const resizeObserver = new ResizeObserver(checkScroll);
        if (listRef.current) resizeObserver.observe(listRef.current);
        return () => resizeObserver.disconnect();
    }, [tabs]);

    return (
        <Tabs
            value={value}
            onChange={(event, newValue) => onChange?.(event, newValue)}
            size={"lg"}
            sx={{
                backgroundColor: 'transparent',
            }}
        >
            <div style={{display: 'flex', alignItems: 'center', position: 'relative'}}>
                <TabList
                    disableUnderline
                    sx={{
                        overflowX: 'auto',
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': {display: 'none'},
                        display: 'flex',
                        whiteSpace: 'nowrap',
                        minWidth: 0
                    }}
                >
                    {stickyTabs.map((tab) => (
                        <Tab
                            key={tab.value}
                            value={tab.value}
                            disableIndicator
                            variant="plain"
                            color="neutral"
                            sx={{
                                position: 'sticky',
                                left: 0,
                                zIndex: 2,
                                minWidth: 100,
                                flexShrink: 0,
                                textTransform: 'none',
                                marginRight: '2px',
                                borderBottom:
                                    value === tab.value
                                        ? `2px solid ${theme.vars.palette.primary[500]}`
                                        : `2px solid #ccc`,
                                backgroundColor: 'transparent',
                                '&[aria-selected="true"]': {
                                    backgroundColor: 'transparent'
                                },
                                '&:not([aria-selected="true"]):hover': {
                                    backgroundColor: 'transparent'
                                }
                            }}
                        >
                            {tab.icon}
                            {tab.label}
                        </Tab>
                    ))}
                </TabList>

                {canScrollLeft && (
                    <IconButton variant="plain" onClick={() => scroll('left')} sx={{ml: 1}}>
                        <ChevronLeftFilledOutlined/>
                    </IconButton>
                )}

                <TabList
                    ref={listRef}
                    onScroll={checkScroll}
                    disableUnderline
                    sx={{
                        overflowX: 'auto',
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': {display: 'none'},
                        display: 'flex',
                        flex: 1,
                        whiteSpace: 'nowrap',
                        minWidth: 0
                    }}
                >
                    {scrollableTabs.map((tab) => (
                        <Tab
                            key={tab.value}
                            value={tab.value}
                            disableIndicator
                            variant="plain"
                            color="neutral"
                            sx={{
                                flexShrink: 0,
                                textTransform: 'none',
                                minWidth: 100,
                                marginRight: '2px',
                                borderBottom:
                                    value === tab.value
                                        ? `2px solid ${theme.vars.palette.primary[500]}`
                                        : `2px solid #ccc`,
                                backgroundColor: 'transparent',
                                '&[aria-selected="true"]': {
                                    backgroundColor: 'transparent'
                                },
                                '&:not([aria-selected="true"]):hover': {
                                    backgroundColor: 'transparent'
                                }
                            }}
                        >
                            {tab.icon}
                            {tab.label}
                        </Tab>
                    ))}
                </TabList>

                {canScrollRight && (
                    <IconButton variant="plain" onClick={() => scroll('right')} sx={{ml: 1}}>
                        <ChevronRightFilledOutlined/>
                    </IconButton>
                )}

                {(onCloseAll || onCloseOthers) && (
                    <Dropdown>
                        <MenuButton
                            slots={{root: IconButton}}
                            slotProps={{root: {variant: 'plain', sx: {ml: 1}}}}
                        >
                            <TabCloseOutlined />
                        </MenuButton>
                        <Menu placement="bottom-end">
                            {onCloseOthers && (
                                <MenuItem onClick={onCloseOthers}>
                                    {t('Close all except current')}
                                </MenuItem>
                            )}
                            {onCloseAll && <MenuItem onClick={onCloseAll}>
                                {t('Close all Tabs')}
                            </MenuItem>}
                        </Menu>
                    </Dropdown>
                )}
            </div>
        </Tabs>
    );
};

export default AutoScrollTabs;
